/** Build details share the Run board's resizable reading surface. */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { SidePanel } from '@/components/SidePanel'
import { MessageComposer, MessageSendButton } from '@/components/MessageComposer'
import { AgentTrace } from '@/components/agent/AgentTrace'
import { AgentIdentity, AgentStatus, isResearcher } from '@/components/agent/AgentJobs'
import { ExperimentRows } from '@/components/agent/ExperimentRows'
import { Budget, NodeChip, RefLink, isLive } from '@/components/agent/job'
import type { BoardJob, Job, TaskExperiment } from '@/components/agent/types'
import { fmtDateTime } from '@/lib/format'
import { gradingIdentity } from '@/lib/runs'

export function AgentPanel({ taskId, jobId, summary, onClose }: {
  taskId: string; jobId: string; summary?: BoardJob; onClose: () => void
}) {
  const [job, setJob] = useState<Job | undefined>(summary)
  const [experiments, setExperiments] = useState<TaskExperiment[] | undefined>(summary?.rollouts)
  const [error, setError] = useState('')
  // What the job works on: a website task, a template (its researcher), or nothing the website knows.
  const [owner, setOwner] = useState<'task' | 'template' | null>()
  const [runsError, setRunsError] = useState('')
  const [resumeMessage, setResumeMessage] = useState('')
  const [resumeBusy, setResumeBusy] = useState(false)
  const [resumeError, setResumeError] = useState('')
  const hasGrading = job?.node?.running?.some(attempt => attempt.attempt_kind === 'expert' || attempt.attempt_kind === 'reference')

  async function resume() {
    if (!resumeMessage.trim() || resumeBusy) return
    setResumeBusy(true); setResumeError('')
    try {
      await api.post(`/eng/tasks/${taskId}/authoring/${jobId}/resume`, { message: resumeMessage.trim() })
      setResumeMessage('')
      const view = await api.get<{ jobs: Job[] }>(`/eng/tasks/${taskId}/authoring?job_id=${encodeURIComponent(jobId)}`)
      setJob(view.jobs.find(j => j.job_id === jobId))
    } catch (e) { setResumeError(String(e)) }
    finally { setResumeBusy(false) }
  }

  useEffect(() => {
    let stopped = false
    let timer: ReturnType<typeof setTimeout>
    async function poll() {
      // A rollout-list failure must not hide an otherwise readable build.
      await api.get<{ jobs: Job[]; owner: 'task' | 'template' | null }>(`/eng/tasks/${taskId}/authoring?job_id=${encodeURIComponent(jobId)}`).then(view => {
          if (stopped) return
          const found = view.jobs.find(j => j.job_id === jobId)
          setJob(found); setOwner(view.owner); setError(found ? '' : 'No such authoring job.')
          // Live attempts arrive with the worker heartbeat. Fetch historical
          // experiment sessions only for finished builders.
          if (found && !isLive(found.status)) {
            return api.get<{ experiments: TaskExperiment[] }>(`/eng/tasks/${taskId}/experiments`).then(runs => {
              if (!stopped) { setExperiments(runs.experiments.filter(r => r.authoring?.job_id === jobId)); setRunsError('') }
            }).catch(e => { if (!stopped) setRunsError(String(e)) })
          }
        }).catch(e => { if (!stopped) setError(String(e)) })
      if (!stopped) timer = setTimeout(poll, 8000)
    }
    void poll()
    return () => { stopped = true; clearTimeout(timer) }
  }, [taskId, jobId])

  return <SidePanel label={`authoring job ${jobId}`} onClose={onClose} head={
    <div className="agent-detail-heading">
      <div className="agent-detail-identity"><AgentIdentity identity={taskId} name={job?.agent_name || 'Verifier build'} researcher={!!job && isResearcher(job)} running={!error && job?.status === 'running' && job.phase !== 'account_admission'} />{job && <AgentStatus status={error ? "unavailable" : job.status} job={job} />}</div>
      <h2>{summary?.title || taskId}</h2>
      {owner && <Link to={`/eng/${owner === 'task' ? 'tasks' : 'templates'}/${taskId}`}>Open {owner} <Icon name="arrow" size={14} /></Link>}
    </div>
  }>
    <div className="agent-detail">
      {error && <div className="note err">{error}</div>}
      {!job && !error && <p className="muted">Loading build…</p>}
      {job && !error && <>
        <div className="agent-detail-meta"><span>Started {fmtDateTime(job.created_at)}</span>{job.finished_at && <span>Finished {fmtDateTime(job.finished_at)}</span>}</div>
        {job.delivery_error && <p className="note err" role="alert">The authoring service refused the last request ({job.delivery_error.code.replaceAll('_', ' ')}): {job.delivery_error.message}</p>}
        {job.blocker && <div className="agent-blocker"><h3>{job.status === 'blocked_expert' ? 'For the expert' : job.status === 'blocked_dev' ? 'For the developer' : 'Why it stopped'}</h3><p>{job.blocker}</p></div>}
        {job.status === 'running' && job.model_retry && <p className="note warn" role="status">
          {job.model_retry.retry_at * 1000 > Date.now()
            ? `The model is temporarily unavailable. Recovery attempt ${job.model_retry.count} is scheduled for ${fmtDateTime(new Date(job.model_retry.retry_at * 1000).toISOString())}.`
            : `Recovering from a temporary model error (attempt ${job.model_retry.count}).`} Your work is retained.
        </p>}
        {job.resume_warning && !job.can_resume && <p className="note warn">{job.resume_warning}</p>}
        {job.can_resume && <section className="agent-resume" aria-label="Send agent back to work">
          <h3>Back to work</h3>
          {job.resume_warning && <p className="note warn">{job.resume_warning}</p>}
          {job.snapshot_pending && <p className="muted">The agent is saving its session. Wait for this to finish before restarting it.</p>}
          <MessageComposer onSend={() => void resume()} sendDisabled={resumeBusy || job.snapshot_pending || !resumeMessage.trim()}
            actions={<MessageSendButton type="button" label="Send back to work" busy={resumeBusy}
              disabled={resumeBusy || job.snapshot_pending || !resumeMessage.trim()} onClick={() => void resume()} />}>
            <textarea aria-label="Message to the agent" placeholder="Give the agent context to continue…" rows={3}
              value={resumeMessage} disabled={resumeBusy} onChange={e => setResumeMessage(e.target.value)} maxLength={4000} />
          </MessageComposer>
          {resumeError && <p className="note err" role="alert">{resumeError}</p>}
        </section>}
        <div className="agent-build-facts">
          {job.limits && <Budget job={job} />}
          {job.node && <div className="agent-detail-node"><NodeChip node={job.node} />{job.node.activity && <span>{job.node.activity}</span>}{job.node.instance_id && <code>{job.node.instance_id}</code>}</div>}
        </div>
        {(job.candidate || job.final) && <div className="agent-artifacts"><RefLink value={job.candidate} label="Candidate" /><RefLink value={job.final} label="Submitted package" /></div>}
        {isLive(job.status) ? <section className="agent-detail-rollouts" aria-label={hasGrading ? 'Running evaluations' : 'Running rollouts'}>
          <div className="agent-detail-section-head"><h3>{hasGrading ? 'Running evaluations' : 'Running rollouts'} <span>{job.node?.running?.length ?? 0}</span></h3></div>
          {job.node?.state === 'unknown' || job.node?.state === 'unreachable'
            ? <p className="muted">Live rollout status is unavailable.</p>
            : job.node?.running?.length ? <div className="rows">{job.node.running.map(attempt => {
                const grading = gradingIdentity(attempt.attempt_kind)
                return <Link
                key={attempt.attempt_id} className="trow" to={`/eng/runs/${job.node?.experiment_id}?job=${attempt.job_id}`}>
                <div><div className="title experiment-model-title"><Icon name={grading?.icon ?? 'agent'} size={grading ? 16 : 20} /><span>{grading?.label ?? (attempt.model || 'Model not recorded')}</span></div>
                  <div className="subline">{grading ? 'Grading' : attempt.steps ? `${attempt.steps} steps` : 'Starting'} · {Math.floor(attempt.elapsed_seconds / 60)}m elapsed</div></div>
                <Icon name="arrow" size={16} />
              </Link>})}</div> : <p className="muted">No rollouts running right now.</p>}
        </section> : <section className="agent-detail-rollouts" aria-label="Rollouts">
          <div className="agent-detail-section-head"><h3>Rollouts {experiments && <span>{experiments.length}</span>}</h3></div>
          {runsError && <p className="note err">Could not refresh rollouts. {runsError}</p>}
          {experiments === undefined
            ? !runsError && <p className="muted">Loading rollouts…</p>
            : <ExperimentRows experiments={experiments} empty={Object.keys(job.experiments ?? {}).length ? 'Waiting for the dispatched rollout to appear.' : 'No rollouts launched yet.'} />}
        </section>}
      </>}
      {job && <AgentTrace taskId={taskId} jobId={job.job_id} live={isLive(job.status)} />}
      <div className="agent-detail-id">Build <code>{jobId}</code></div>
    </div>
  </SidePanel>
}
